import type { IComponentController, IOnChangesObject } from "angular";
import "angular-translate";
import { isEqual } from "lodash";
import type { IEvent, IFunctionality, IIssue, ISiteEquipmentNormalized, ISiteSlim } from "App/Modules/Models/Models";
import type { Moment } from "moment-timezone";
import { utc } from "moment-timezone";

import type ITimeLineEventAlarm from "./Models/ITimeLineEventAlarm";
import type ITimeLineEventIssue from "./Models/ITimeLineEventIssue";
import type ITimeLineSiteEquipmentEntry from "./Models/ITimeLineSiteEquipmentEntry";

const timeLineWidth = 1000;
const maxDaySegments = 8;

// Implementation note: This class uses the internal value of the Moment time (which is UTC unix timestamp)
// when converting between times and pixels, so as long as the inputs are correct (expressed in either UTC
// or local time with corret time zone attached), the time zone set in the timestamps don't matter
export default class EventsTimeLineComponentController implements IComponentController {
    public static $inject: string[] = [];

    private static getGridDays(dateFrom: Moment, dateTo: Moment) {
        const daysInInterval = dateTo.clone().diff(dateFrom, "days");
        const daysBetweenLines = Math.ceil(daysInInterval / maxDaySegments);

        const days = [];
        for (let d = 0; d < daysInInterval; d += daysBetweenLines) {
            days.push(dateFrom.clone().add(d, "days"));
        }

        return days;
    }

    private static convertDateToRelativeCoordinates(dateFrom: Moment, dateTo: Moment, date: Moment) {
        if (date === null) {
            return timeLineWidth;
        }

        const fullMsRange = dateTo.valueOf() - dateFrom.valueOf();
        const relativeMs = date.valueOf() - dateFrom.valueOf();

        let x = Math.floor((relativeMs / fullMsRange) * timeLineWidth);
        x = x < 0 ? 0 : x;
        x = x > timeLineWidth ? timeLineWidth : x;
        return x;
    }

    public events: IEvent[];

    public issues: IIssue[];

    public siteEquipment: ISiteEquipmentNormalized[];

    public site: ISiteSlim;

    public functionalities: IFunctionality[];

    public dateFrom: Moment;

    // Timestamp of the rightmost time point shown on the timeline, so if e.g. 2020 - 05 - 22 is
    // selected in the date picker, dateTo will be 2020-05-23 00:00:00 (local time).
    public dateTo: Moment;

    public timeLineSiteEquipmentEntries: ITimeLineSiteEquipmentEntry[];

    public $onChanges(changes: IOnChangesObject) {
        if (
            (this.siteEquipment &&
                changes.siteEquipment &&
                !isEqual(changes.siteEquipment.previousValue, changes.siteEquipment.currentValue)) ||
            (this.functionalities &&
                changes.functionalities &&
                !isEqual(changes.functionalities.previousValue, changes.functionalities.currentValue)) ||
            (this.events && changes.events && !isEqual(changes.events.previousValue, changes.events.currentValue)) ||
            (this.issues && changes.issues && !isEqual(changes.issues.previousValue, changes.issues.currentValue))
        ) {
            if (!this.siteEquipment || !this.functionalities || !this.events || !this.issues) {
                return;
            }

            this.timeLineSiteEquipmentEntries = this.siteEquipment
                .sort((a, b) => a.class.valueOf() - b.class.valueOf())
                .map((se) => {
                    const alarms = this.events
                        .filter((event) => event.siteEquipmentId === se.id)
                        .map((event) => {
                            return {
                                coordX: this.getAlarmCoords(event),
                                dtEvent: event.dtEvent,
                                id: event.friendlyId,
                            } as ITimeLineEventAlarm;
                        });

                    const issues = this.issues
                        .filter(
                            (issue) =>
                                issue.affectedMainSiteEquipment?.id === se.id ||
                                issue.source.id === se.id ||
                                issue.source.id === se.equipmentId,
                        )
                        .map((issue) => {
                            return {
                                dtEnded: issue.dtIssue.clone().add(issue.issueDurationSeconds, "s"),
                                dtStarted: issue.dtIssue,
                                durationSeconds: issue.issueDurationSeconds,
                                id: issue.id,
                                isAcknowledged: issue.isAcknowledged,
                                isActive: issue.isActive,
                                severityName: issue.severityName,
                                startX: this.getOperationalIssueStart(issue),
                                stopX: this.getOperationalIssueStop(issue),
                            } as ITimeLineEventIssue;
                        });

                    return {
                        alarms,
                        issues,
                        siteEquipmentId: se.id,
                        siteEquipmentName: se.name,
                    } as ITimeLineSiteEquipmentEntry;
                })
                .filter((se) => se.alarms.length > 0 || se.issues.length > 0);
        }
    }

    public getAlarmCoords(event: IEvent) {
        return EventsTimeLineComponentController.convertDateToRelativeCoordinates(
            this.dateFrom,
            this.dateTo,
            event.dtEvent,
        );
    }

    public getOperationalIssueStart(issue: IIssue) {
        const startCoord = EventsTimeLineComponentController.convertDateToRelativeCoordinates(
            this.dateFrom,
            this.dateTo,
            issue.dtIssue,
        );

        return startCoord;
    }

    public getOperationalIssueStop(issue: IIssue) {
        if (issue.isActive) {
            return this.getTodayCoordX();
        }

        if (issue.issueDurationSeconds == null) {
            // Should not happen if isActive == false, but if it does, this issue is probably broken somehow
            console.error(`issueDurationSeconds == null for issue ${issue.id}`);
            return this.getOperationalIssueStart(issue);
        }

        const dtIssueEnded = issue.dtIssue.clone().add(issue.issueDurationSeconds, "s");

        let stopCoord = EventsTimeLineComponentController.convertDateToRelativeCoordinates(
            this.dateFrom,
            this.dateTo,
            dtIssueEnded,
        );

        stopCoord = stopCoord < this.getTodayCoordX() ? stopCoord : this.getTodayCoordX();

        return stopCoord;
    }

    public getGridLinesCoords() {
        return EventsTimeLineComponentController.getGridDays(this.dateFrom, this.dateTo).map((day: Moment) => {
            return EventsTimeLineComponentController.convertDateToRelativeCoordinates(this.dateFrom, this.dateTo, day);
        });
    }

    public getGridDates() {
        if (!this.dateFrom || !this.dateTo) {
            // todo: Previously returned nothing (lint error). [] might be better option.
            return undefined;
        }
        return EventsTimeLineComponentController.getGridDays(this.dateFrom, this.dateTo.clone().add(1, "days")).map(
            (date) => date.format(),
        );
    }

    public getGridDateCoords(date: Moment) {
        return EventsTimeLineComponentController.convertDateToRelativeCoordinates(
            this.dateFrom,
            this.dateTo,
            utc(date),
        );
    }

    public getTodayCoordX() {
        return EventsTimeLineComponentController.convertDateToRelativeCoordinates(this.dateFrom, this.dateTo, utc());
    }

    public showTodayLine() {
        return utc().isBetween(this.dateFrom, this.dateTo, null, "[)");
    }
}
